import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { includes, map } from 'lodash'
import categoryService from '../services/category.service'
import itemPrintableService from '../services/itemPrintable.service'
import ItemContactInput, {
  ItemContactInputData
} from '../partials/ItemContactInput'
import ItemMaterialInput, {
  ItemMaterialInputData
} from '../partials/ItemMaterialInput'
import materialService from '../services/material.service'
import itemService from '../services/item.service'
import categoryApi, { CategoryData } from '../apis/category.api'
import { ContactableType } from '../apis/contact.api'
import itemApi, { ItemCreateRequest } from '../apis/item.api'
import { ItemType } from '../apis/ItemType.api'
import { ItemPrintableName } from '../apis/ItemPrintable.api'
import routes from '../routes/routes'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import Row from '../components/Row/Row'
import InputText from '../components/InputText'
import Select from '../components/Select'
import PageHeading from '../components/PageHeading'
import CheckboxGroup from '../components/CheckboxGroup'

interface Props extends RouteComponentProps {}

interface State {
  code: string
  category: string
  types: ItemType[]
  description: string
  value: string
  netWeight: string
  grossWeight: string
  cpp: string
  qpc: string
  cpb: string
  safetyStock: string
  categories: CategoryData[]
  labels: ItemPrintableName[]
  materials: ItemMaterialInputData[]
  vendors: ItemContactInputData[]
  customers: ItemContactInputData[]
}

class ItemCreate extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      code: '',
      category: '',
      types: [],
      description: '',
      value: '',
      netWeight: '',
      grossWeight: '',
      cpp: '',
      qpc: '',
      cpb: '',
      safetyStock: '',
      categories: [],
      labels: ['pallet', 'cases'],
      materials: [],
      vendors: [],
      customers: []
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleTypesChange = this.handleTypesChange.bind(this)
    this.handleCreateClick = this.handleCreateClick.bind(this)
    this.handleContactChange = this.handleContactChange.bind(this)
    this.handleMaterialChange = this.handleMaterialChange.bind(this)
  }

  get categoryOptions () {
    return categoryService.selectOptions(this.state.categories)
  }

  get vendorInput () {
    return includes(this.state.types, 'receivable') &&
      <ItemContactInput
        type="vendor"
        addeds={this.state.vendors}
        onChange={this.handleContactChange} />
  }

  get customerInput () {
    return includes(this.state.types, 'production') &&
      <ItemContactInput
        type="customer"
        addeds={this.state.customers}
        onChange={this.handleContactChange} />
  }

  create () {
    const { code, category, description, netWeight, grossWeight } = this.state
    const { cpb, cpp, qpc, safetyStock, value, types, labels } = this.state
    const contactData = (contacts: ItemContactInputData[]) => {
      return map(contacts, contact => {
        return { site: contact.site.id, partNumber: contact.partNumber }
      })
    }
    const materials = materialService.payloadData(this.state.materials)
    const customers = contactData(this.state.customers)
    const vendors = contactData(this.state.vendors)
    const payload: ItemCreateRequest = {}
    if (code) payload.code = code
    if (category) payload.category = Number(category)
    if (types) payload.types = types
    if (description) payload.description = description
    if (value) payload.value = Number(value)
    if (netWeight) payload.netWeight = Number(netWeight)
    if (grossWeight) payload.grossWeight = Number(grossWeight)
    if (cpp) payload.cpp = Number(cpp)
    if (qpc) payload.qpc = Number(qpc)
    if (cpb) payload.ppc = Number(cpb)
    if (safetyStock) payload.safetyStock = Number(safetyStock)
    if (labels) payload.labels = labels
    if (materials) payload.materials = materials
    if (customers) payload.customers = customers
    if (vendors) payload.vendors = vendors
    itemApi.create(payload, item => {
      if (!item) return
      this.props.history.push(routes.items)
    })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'code') this.setState({ code: value })
    if (name === 'category') this.setState({ category: value })
    if (name === 'description') this.setState({ description: value })
    if (name === 'value') this.setState({ value })
    if (name === 'netWeight') this.setState({ netWeight: value })
    if (name === 'grossWeight') this.setState({ grossWeight: value })
    if (name === 'cpp') this.setState({ cpp: value, cpb: value })
    if (name === 'qpc') this.setState({ qpc: value })
    if (name === 'cpb') this.setState({ cpb: value })
    if (name === 'safetyStock') this.setState({ safetyStock: value })
  }

  handleTypesChange (types: ItemType[]) {
    this.setState({ types })
  }

  handleLabelsChange = (labels: ItemPrintableName[]) => {
    this.setState({ labels })
  }

  handleContactChange (type: ContactableType, addeds: ItemContactInputData[]) {
    if (type === 'customer') this.setState({ customers: addeds })
    else if (type === 'vendor') this.setState({ vendors: addeds })
  }

  handleMaterialChange (materials: ItemMaterialInputData[]) {
    this.setState({ materials })
  }

  handleCreateClick () {
    this.create()
  }

  listCategories () {
    categoryApi.list(categories => {
      if (!categories) return
      this.setState({ categories })
    })
  }

  componentDidMount () {
    this.listCategories()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading
              text={i18n("Create Item")}
              doc='items-create-item' />
            <Row column={1}>
              <InputText
                name="code"
                value={this.state.code}
                label={i18n("Item code")}
                onChange={this.handleInputChange} />
            </Row>
            <Row column={2}>
              <Select
                name="category"
                value={this.state.category}
                options={this.categoryOptions}
                label={i18n("Category")}
                onChange={this.handleInputChange} />
              <CheckboxGroup
                checks={itemService.typeChecks}
                checkeds={this.state.types}
                label={i18n("Types")}
                onChange={this.handleTypesChange} />
            </Row>
            <Row column={1}>
              <InputText
                name="description"
                value={this.state.description}
                label={i18n("Description")}
                onChange={this.handleInputChange} />
            </Row>
            <Row column={2}>
              <InputText
                name="netWeight"
                value={this.state.netWeight}
                label={i18n("Net weight")}
                onChange={this.handleInputChange} />
              <InputText
                name="grossWeight"
                value={this.state.grossWeight}
                label={i18n("Gross weight")}
                onChange={this.handleInputChange} />
              <InputText
                name="cpp"
                value={this.state.cpp}
                label={i18n("Cases per pallet (CPP)")}
                onChange={this.handleInputChange} />
              <InputText
                name="qpc"
                value={this.state.qpc}
                label={i18n("Quantity per case (QPC)")}
                onChange={this.handleInputChange} />
              <InputText
                name="cpb"
                value={this.state.cpb}
                label={i18n("Packs per case (PPC)")}
                onChange={this.handleInputChange} />
              <InputText
                name="safetyStock"
                value={this.state.safetyStock}
                label={i18n("Safety stock (minimum)")}
                onChange={this.handleInputChange} />
              <InputText
                type="number"
                name="value"
                value={this.state.value}
                label={i18n("Item value")}
                onChange={this.handleInputChange} />
              <CheckboxGroup
                checks={itemPrintableService.labelChecks}
                checkeds={this.state.labels}
                label={i18n("Labels")}
                onChange={this.handleLabelsChange} />
            </Row>
            <ItemMaterialInput
              addeds={this.state.materials}
              onChange={this.handleMaterialChange} />
            {this.vendorInput}
            {this.customerInput}
            <CardButton
              proceedText={i18n("Create")}
              cancelText={i18n("Cancel")}
              onProceedClick={this.handleCreateClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}

export default withRouter(ItemCreate)
